import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App } from 'antd'
import { useTranslation } from 'react-i18next'
import { api, coverLeft, day, money, type Policy } from '../api'
import { useSession } from '../session'
import { ClaimTable } from './Claims'

/**
 * The holder's balance and everything held against it: money, cover, bonds,
 * what is owed on claims, and what they have earned by referring. The service
 * is built around the balance because more of what a customer holds — a rent
 * bond already, more later — lives here. It is not called a wallet.
 */
export default function Balance() {
  const { t } = useTranslation()
  const { me, account } = useSession()
  const navigate = useNavigate()

  const policies = useQuery({ queryKey: ['policies'], queryFn: () => api.policies(), refetchInterval: 15_000 })
  const claims = useQuery({ queryKey: ['claims'], queryFn: () => api.claims(), refetchInterval: 15_000 })
  const external = useQuery({ queryKey: ['external'], queryFn: api.external, staleTime: 60_000 })
  const referrals = useQuery({ queryKey: ['referrals', 'mine'], queryFn: api.referralsMine, staleTime: 60_000 })

  const all = policies.data?.data ?? []
  const mine = all.filter(p => p.holder === account)
  const forMe = all.filter(p => p.kind === 'bond' && p.beneficiary === account && p.holder !== account)
  const order = (p: Policy) => (p.status === 'active' ? 0 : p.status === 'cancelled' ? 1 : 2)
  const sorted = [...mine].sort((a, b) => order(a) - order(b))
  const earned = Number(referrals.data?.earned ?? 0)
  const bond = Number(me?.balances?.BOND ?? 0)

  return (
    <div className="page page-mid stack">
      <div className="panel">
        <div className="panel-body wallet">
          <div className="figure">
            <span className="label">{t('balance')}</span>
            <span className="value num">{money(me?.balances?.STABLE)}</span>
          </div>
          {bond > 0 && (
            <div className="figure">
              <span className="label">{t('heldAsBond')}</span>
              <span className="value num">{money(bond)}</span>
            </div>
          )}
          <div className="figure">
            <span className="label">{t('owedOnClaims')}</span>
            <span className="value num">{money(me?.balances?.CLAIM)}</span>
          </div>
          {(external.data?.data ?? []).map(x => (
            <div className="figure" key={x.source}>
              <span className="label">
                {x.label} {x.test && <span className="chip test">{t('testFigure')}</span>}
              </span>
              <span className="value num">{money(x.amount)}</span>
            </div>
          ))}
          <span className="topbar-spacer" />
          <button className="submit buy" style={{ width: 'auto', padding: '10px 18px' }} onClick={() => navigate('/products')}>
            {t('buyCover')}
          </button>
        </div>
      </div>

      <div className="panel">
        <div className="panel-head"><span className="grow">{t('myCover')}</span></div>
        <div className="panel-body">
          {policies.isLoading && <div className="skeleton" style={{ height: 90 }} />}
          {!policies.isLoading && sorted.length === 0 && <p className="empty">{t('noCover')}</p>}
          <div className="cards">
            {sorted.map(p => <PolicyCard key={p.id} policy={p} />)}
          </div>
        </div>
      </div>

      {forMe.length > 0 && (
        <div className="panel">
          <div className="panel-head"><span className="grow">{t('bondsForMe')}</span></div>
          <div className="panel-body">
            <div className="cards">
              {forMe.map(p => <PolicyCard key={p.id} policy={p} asLandlord />)}
            </div>
          </div>
        </div>
      )}

      <div className="panel">
        <div className="panel-head">
          <span className="grow">{t('myClaims')}</span>
          <Link className="btn-ghost" to="/claims">{t('allClaims')}</Link>
        </div>
        <div className="panel-body flush">
          {!claims.isLoading && (claims.data?.data.length ?? 0) === 0 && <p className="empty">{t('noClaims')}</p>}
          {(claims.data?.data.length ?? 0) > 0 && <ClaimTable claims={claims.data!.data.slice(0, 5)} />}
        </div>
      </div>

      {(earned > 0 || (referrals.data?.codes.length ?? 0) > 0) && (
        <div className="panel">
          <div className="panel-head"><span className="grow">{t('referralIncome')}</span></div>
          <div className="panel-body wallet">
            <div className="figure">
              <span className="label">{t('earned')}</span>
              <span className="value num">{money(earned)}</span>
            </div>
            <div className="figure">
              <span className="label">{t('referredSales', { count: referrals.data?.policies.length ?? 0 })}</span>
              <span className="value">{(referrals.data?.codes ?? []).map(c => c.code).join(', ') || account}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/** A policy as a card: cover — or a bond — with what is left of it and when it ends. */
export function PolicyCard({ policy, asLandlord = false }: { policy: Policy; asLandlord?: boolean }) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const qc = useQueryClient()
  const { me } = useSession()
  const [confirming, setConfirming] = useState(false)
  const [pin, setPin] = useState('')
  const bond = policy.kind === 'bond'

  const cancel = useMutation({
    mutationFn: () => api.cancel(policy.id, pin || undefined),
    onSuccess: r => {
      setConfirming(false); setPin('')
      message.success(`${t('cancelled')} · ${t('refunded')} ${money(r.policy.refunded)}`)
      qc.invalidateQueries({ queryKey: ['policies'] })
      qc.invalidateQueries({ queryKey: ['me'] })
    },
    onError: (e: Error) => message.error(e.message),
  })

  const left = coverLeft(policy)
  const claimedOn = Number(policy.claimed) > 0
  const ended = policy.status === 'cancelled' ? policy.cancelled_at : policy.ends_at

  return (
    <div className="card">
      <div className="card-head">
        <span className="chip">{bond ? t('bondWord') : t('coverWord')}</span>
        <Link className="title" to={`/policies/${policy.id}`}>{policy.product_name || policy.product}</Link>
        <span className="grow" />
        <span className={`state ${policy.status}`}>{t(policy.status)}</span>
      </div>

      <div className="meter" title={`${money(policy.remaining)} / ${money(policy.coverage)}`}>
        <span style={{ width: `${Math.round(left * 100)}%` }} />
      </div>

      <div className="figures">
        <div className="figure">
          <span className="label">{bond ? t('inEscrow') : t('coverLeft')}</span>
          <span className="value num">{money(policy.status === 'released' ? 0 : policy.remaining)}</span>
        </div>
        <div className="figure">
          <span className="label">{bond ? t('bondAmount') : t('sumInsured')}</span>
          <span className="value num">{money(policy.coverage)}</span>
        </div>
        {bond ? (
          <div className="figure">
            <span className="label">{asLandlord ? t('renter') : t('landlord')}</span>
            <span className="value">{asLandlord ? policy.holder : policy.beneficiary}</span>
          </div>
        ) : (
          <div className="figure">
            <span className="label">{t('premiumWord')}</span>
            <span className="value num">{money(policy.premium)}</span>
          </div>
        )}
        {bond && policy.status !== 'active' ? (
          <div className="figure">
            <span className="label">{policy.status === 'released' ? t('releasedWord') : t('releaseOn')}</span>
            <span className="value">{policy.status === 'released' ? money(policy.released) : day(policy.release_at)}</span>
          </div>
        ) : (
          <div className="figure">
            <span className="label">{policy.status === 'cancelled' ? t('cancelled') : t('ends')}</span>
            <span className="value">{day(ended)}</span>
          </div>
        )}
      </div>

      {policy.status === 'active' && !bond && !asLandlord && (
        confirming ? (
          <div className="stack" style={{ gap: 8 }}>
            <p className="field-hint">
              {claimedOn ? t('cancelClaimed')
                : policy.refund === 'pro_rata' ? t('cancelProRata') : t('cancelNoRefund')}
            </p>
            {me?.pin?.set && !claimedOn && (
              <div className="input-wrap">
                <input value={pin} onChange={e => setPin(e.target.value)} inputMode="numeric"
                  autoComplete="off" placeholder={t('pin')} aria-label={t('pin')} />
              </div>
            )}
            <div className="row">
              <button className="btn-ghost" onClick={() => setConfirming(false)}>{t('cancel')}</button>
              {!claimedOn && (
                <button className="btn-small" disabled={cancel.isPending} onClick={() => cancel.mutate()}>
                  {t('cancelCover')}
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="row">
            <span className="muted" style={{ fontSize: 12 }}>{t('policyId')} {policy.id}</span>
            <span className="topbar-spacer" />
            <button className="btn-ghost" onClick={() => setConfirming(true)}>{t('cancelCover')}</button>
          </div>
        )
      )}
    </div>
  )
}
